import { useState } from 'react';
import { Button, Card, Col, Descriptions, Modal, Progress, Row, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Recommendation } from '@noumase/cylinder-calc';
import { pickSeriesByThrust } from './cylinder-series';
import CylinderModelViewer from './CylinderModelViewer';

const { Title, Text } = Typography;

/** 把功率值格式化为友好字符串（与 ReversePanel 保持一致） */
function formatPower(w: number): string {
  return w >= 1000 ? `${(w / 1000).toString().replace(/\.?0+$/, '')}KW` : `${w}W`;
}

function formatRatio(r: Recommendation['ratio']): string {
  return `${r.reducer} × ${r.syncDec} = ${r.total.toFixed(3)}`;
}

interface Props {
  rec: Recommendation;
  rank: number;
  /** 来自 Top-N 的归一化匹配度（百分比，0~100） */
  matchPercent: number;
  /** 整体最优原始得分（用于显示 raw score） */
  rawScore: number;
}

/**
 * 选型方案卡片（参考米思米样式）：
 * 左：缸体占位图 + 排名徽章
 * 中：电机详情（品牌 / 系列 / 功率 / 额定扭矩 / 转速）+ 推力/速度实际值 + 传动配置
 * 右：匹配度环 + CPQ 编码
 */
export function RecommendationCard({ rec, rank, matchPercent, rawScore }: Props) {
  const { t } = useTranslation();
  const isBest = rank === 1;
  const [open, setOpen] = useState(false);
  // 选型算法不输出缸体系列，这里按方案实际推力命中对应系列（“按推力推荐”）
  const series = pickSeriesByThrust(rec.actual.thrust);
  return (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        borderColor: isBest ? '#52c41a' : undefined,
        borderWidth: isBest ? 2 : 1,
      }}
      styles={{ body: { padding: 16 } }}
    >
      <Row gutter={16} align="middle">
        {/* 左：电缸占位图 + 排名徽章 */}
        <Col xs={24} sm={5} md={4}>
          <div
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1 / 1',
              maxWidth: 140,
              margin: '0 auto',
              background: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 36,
              color: '#1677ff',
            }}
          >
            {/* 简单 SVG 电缸示意图 */}
            <svg width="60%" height="60%" viewBox="0 0 100 100" fill="none">
              <rect x="10" y="35" width="60" height="30" rx="3" fill="#1677ff" opacity="0.85" />
              <rect x="70" y="40" width="20" height="20" rx="2" fill="#1677ff" opacity="0.6" />
              <rect x="5" y="42" width="6" height="16" rx="1" fill="#1677ff" />
              <circle cx="22" cy="50" r="3" fill="#fff" />
              <circle cx="40" cy="50" r="3" fill="#fff" />
              <circle cx="58" cy="50" r="3" fill="#fff" />
            </svg>
            <Tag
              color={isBest ? 'green' : 'blue'}
              style={{
                position: 'absolute',
                top: 6,
                left: 6,
                margin: 0,
                fontWeight: 600,
              }}
            >
              {t('reverse.table.rankBadge', { n: rank })}
            </Tag>
          </div>
          {series && (
            <div style={{ marginTop: 10, textAlign: 'center' }}>
              <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
                {series.series}
              </Tag>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                {t('reverse.product.seriesByThrust')}
              </div>
              <Button
                type="link"
                size="small"
                style={{ padding: '2px 0 0' }}
                onClick={() => setOpen(true)}
              >
                {t('reverse.product.view3d')}
              </Button>
            </div>
          )}
        </Col>

        {/* 中：电机详情 + 推力/速度 + 传动 */}
        <Col xs={24} sm={13} md={14}>
          <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
            {rec.motor.brand}
            {rec.motor.series ? ` · ${rec.motor.series}` : ''} · {formatPower(rec.motor.power)}
          </Title>
          <Descriptions
            size="small"
            column={{ xs: 2, sm: 2, md: 3 }}
            colon={false}
            styles={{
              label: { color: '#888', fontSize: 12, paddingBottom: 0 },
              content: { fontWeight: 600, paddingBottom: 8 },
            }}
          >
            <Descriptions.Item label={t('reverse.expand.ratedTorque')}>
              {rec.motor.ratedTorque.toFixed(2)} N·m
            </Descriptions.Item>
            <Descriptions.Item label={t('reverse.expand.ratedSpeed')}>
              {rec.motor.ratedSpeed} r/min
            </Descriptions.Item>
            <Descriptions.Item label={t('reverse.table.lead')}>{rec.lead} mm</Descriptions.Item>
            <Descriptions.Item label={t('reverse.table.thrust')}>
              <Space size={4}>
                {rec.actual.thrust.toFixed(1)} kg
                <Tag color="success" style={{ marginLeft: 0 }}>
                  +{rec.margin.thrust.toFixed(1)}%
                </Tag>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('reverse.table.speed')}>
              <Space size={4}>
                {rec.actual.speed.toFixed(1)} mm/s
                <Tag color="success" style={{ marginLeft: 0 }}>
                  +{rec.margin.speed.toFixed(1)}%
                </Tag>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('reverse.table.ratio')}>
              {formatRatio(rec.ratio)}
            </Descriptions.Item>
          </Descriptions>
          <Space size={8} style={{ marginTop: 4 }} wrap>
            <Tag>
              {t('reverse.expand.brakeOption')}：
              {rec.motor.hasBrakeOption
                ? t('reverse.expand.brakeAvail')
                : t('reverse.expand.brakeNone')}
            </Tag>
            <Tag color="geekblue">
              {t('reverse.expand.cpqCode')}：{rec.motor.cpqMotorBrandCode}
              {rec.motor.cpqMotorPowerCode}
            </Tag>
          </Space>
        </Col>

        {/* 右：匹配度环 + raw score */}
        <Col xs={24} sm={6} md={6} style={{ textAlign: 'center' }}>
          <Progress
            type="circle"
            percent={Math.round(matchPercent)}
            size={88}
            strokeColor={isBest ? '#52c41a' : '#1677ff'}
            format={(p) => (
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 20, fontWeight: 600 }}>{p}%</div>
                <div style={{ fontSize: 11, color: '#888' }}>
                  {t('reverse.table.match')}
                </div>
              </div>
            )}
          />
          <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 6 }}>
            {t('reverse.table.rawScoreTip', { v: rawScore.toFixed(4) })}
          </Text>
        </Col>
      </Row>
      {series && (
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          footer={null}
          width={720}
          title={t('reverse.product.modelTitle', { series: series.series })}
        >
          {open && <CylinderModelViewer url={series.modelUrl} height={460} />}
        </Modal>
      )}
    </Card>
  );
}
